import { makeAutoObservable } from 'mobx'
import type { ICardFilter } from 'shared/ui'
import type { Campaign, IResponseModifyCampaignContactState } from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { CampaignContactsStatisticsTableStore } from 'pages/campaigns/ui/detail/CampaignContactsStatisticsTable'

export class CampaignOverviewStore {
  private _campaignContactsStatisticsTableStore: CampaignContactsStatisticsTableStore
  private _filterKey: IResponseModifyCampaignContactState = 'enrolled'

  constructor(private _campaign: Campaign, private _entities: IEntitiesStore) {
    this._campaignContactsStatisticsTableStore = new CampaignContactsStatisticsTableStore(
      this._campaign,
      this._entities
    )

    makeAutoObservable(this)
  }

  get campaignContactsStatisticsTableStore() {
    return this._campaignContactsStatisticsTableStore
  }

  get filterKey() {
    return this._filterKey
  }

  dispose = () => {
    this._campaignContactsStatisticsTableStore.dispose()
  }

  setFilter = (filter: ICardFilter<typeof this._filterKey>) => {
    if (filter.key === 'enrolled') {
      this._campaignContactsStatisticsTableStore.setFilterState(null)
    } else {
      this._campaignContactsStatisticsTableStore.setFilterState(filter.key)
    }
    this._filterKey = filter.key
  }

  get statistics() {
    return this._campaign.statistics
  }

  get _filters(): Array<ICardFilter<typeof this._filterKey> | false> {
    const quietHoursFilter: ICardFilter<IResponseModifyCampaignContactState> | null =
      this._entities?.featureFlagsStore.quiet_hours &&
      this._entities.organizationStore.quietHoursSettings?.is_active
        ? {
            key: 'quiet_hours',
            active: this._filterKey === 'quiet_hours',
            amount: this.statistics?.quiet_hours_count,
            percentage: this.statistics?.quiet_hours_percent,
            title: 'Quiet hours',
          }
        : null

    return [
      {
        key: 'enrolled',
        active: this._filterKey === 'enrolled',
        amount: this.statistics?.enrolled_contacts_count,
        percentage: this.statistics?.enrolled_contacts_percent,
        title: 'Enrolled',
      },
      {
        key: 'in_progress',
        active: this._filterKey === 'in_progress',
        amount: this.statistics?.in_progress,
        percentage: this.statistics?.in_progress_percent,
        title: 'In progress',
      },
      {
        key: 'completed',
        active: this._filterKey === 'completed',
        amount: this.statistics?.completed,
        percentage: this.statistics?.completed_percent,
        title: 'Completed',
      },
      {
        key: 'unsubscribed',
        active: this._filterKey === 'unsubscribed',
        amount: this.statistics?.unsubscribed,
        percentage: this.statistics?.unsubscribed_percent,
        title: 'Unsubscribed',
      },
      {
        key: 'stopped_by_user',
        active: this._filterKey === 'stopped_by_user',
        amount: this.statistics?.stopped_by_user,
        percentage: this.statistics?.stopped_by_user_percent,
        title: 'Stopped by user',
      },
      ...(quietHoursFilter ? [quietHoursFilter] : []),
      {
        key: 'failed-messages',
        active: this._filterKey === 'failed-messages',
        amount: this.statistics?.failed_messages_count,
        percentage: null,
        title: 'Failed messages',
      },
    ]
  }

  get filters(): ICardFilter<typeof this._filterKey>[] {
    const filters: ICardFilter<typeof this._filterKey>[] = []
    this._filters.forEach((filter) => {
      if (filter) {
        filters.push(filter)
      }
    })
    return filters
  }
}
